import { useCallback, useEffect, useMemo, useState } from 'react'
import { ChevronDown, ChevronUp, Filter } from 'lucide-react'
import { erfuelltRecht } from '@symphenia/shared-types'
import { FilterBar } from '@/components/list/FilterBar'
import type { ActiveFilter, FilterFieldDef } from '@/components/list/list.types'
import { useAuthStore } from '@/lib/auth.store'
import { apiErrorText } from '@/lib/api-error'
import { pageWide } from '@/lib/form-styles'
import { useTerminology } from '@/lib/useTerminology'
import { fetchSites, fetchUsers } from '../api/projects-head.api'
import { fetchBoard, fetchCard, fetchCockpits, type CockpitFilter } from '../api/work-cockpit.api'
import type { CockpitBoard as Board, CockpitCard, CockpitPhase } from '../types/cockpit.types'
import type { LookupSite, LookupUser } from '../types/project.types'
import { CockpitBoard } from '../components/cockpit/CockpitBoard'
import { CockpitList } from '../components/cockpit/CockpitList'
import { CockpitWeek } from '../components/cockpit/CockpitWeek'
import { CockpitDetailPanel } from '../components/cockpit/CockpitDetailPanel'
import { AssignDialog } from '../components/cockpit/AssignDialog'
import { filterAnzahl } from '../components/arbeitsschritte-werkzeuge'

type Sicht = 'board' | 'list' | 'week'
type Ansicht = { phaseKey?: string; sicht?: Sicht; filterOffen?: boolean }

/**
 * Reiter, Sicht und ob die Filterleiste offen steht, merkt sich der Browser, je
 * Benutzer. Die serverseitigen UI-Einstellungen (AP-23) nehmen nur Listen-Layouts
 * an — ein Schlüssel außerhalb ihres Musters würde beim Abgleich verworfen.
 */
const ansichtKey = (userId?: string) => `symphenia.workCockpit.${userId ?? 'anon'}`

function ladeAnsicht(userId?: string): Ansicht {
  try {
    return JSON.parse(localStorage.getItem(ansichtKey(userId)) ?? '{}') as Ansicht
  } catch {
    return {}
  }
}

const SICHTEN: { key: Sicht; label: string }[] = [
  { key: 'board', label: 'Karten' },
  { key: 'list', label: 'Liste' },
  { key: 'week', label: 'Woche' },
]

const STATUS_OPTIONEN = [
  { value: 'PLANNED', label: 'nicht freigegeben' },
  { value: 'READY', label: 'freigegeben' },
  { value: 'ASSIGNED', label: 'zugeteilt' },
  { value: 'IN_PROGRESS', label: 'in Arbeit' },
  { value: 'PAUSED', label: 'pausiert' },
  { value: 'DONE', label: 'fertig' },
]

/**
 * AP-35 Detailblatt 11 — **Arbeitsschritt-Cockpits** (`/work-cockpit`).
 *
 * Ein Nav-Eintrag mit Phasen-Reitern statt vier Einträgen (12.2-3); jeder
 * Reiter ist ein Cockpit mit drei Sichten. Reiter und Sicht werden gemerkt.
 * Die Filter gelten für Board und Liste zugleich; „nur freigegebene
 * Positionen" ist die Vorgabe (Abnahme-Frage 8) — Positionen in Vorbereitung
 * erscheinen erst mit ihrer Ausführungsfreigabe.
 */
export default function WorkCockpitPage() {
  const terms = useTerminology()
  const { user } = useAuthStore()
  const darf = (recht: string) => erfuelltRecht(recht, user?.permissions)
  const darfEinteilen = darf('projects.assign.manage')
  const darfFreigeben = darf('projects.approve.execution')
  const darfStatus = darf('projects.workstep.manage') || darf('projects.position.manage')

  const gemerkt = ladeAnsicht(user?.id)
  const [phasen, setPhasen] = useState<CockpitPhase[]>([])
  const [phaseKey, setPhaseKey] = useState<string>(gemerkt.phaseKey ?? '')
  const [sicht, setSicht] = useState<Sicht>(gemerkt.sicht ?? 'board')
  // Auftraggeber 15.10.2026: Die Leiste lässt sich wegklappen, damit das Board den Platz bekommt.
  // Ohne Wahl steht sie offen — so stand sie bisher.
  const [filterOffen, setFilterOffen] = useState(gemerkt.filterOffen ?? true)
  const [filterValues, setFilterValues] = useState<Record<string, ActiveFilter>>({})
  const [board, setBoard] = useState<Board | null>(null)
  const [benutzer, setBenutzer] = useState<LookupUser[]>([])
  const [standorte, setStandorte] = useState<LookupSite[]>([])
  const [fehler, setFehler] = useState<string | null>(null)
  const [version, setVersion] = useState(0)
  const [detail, setDetail] = useState<{ karte: CockpitCard; liste: CockpitCard[] } | null>(null)
  const [einteilen, setEinteilen] = useState<{ stepIds: string[]; vorbelegt?: { userId?: string; startsOn?: string; endsOn?: string } } | null>(null)

  useEffect(() => {
    fetchCockpits()
      .then((r) => {
        setPhasen(r.phases)
        setPhaseKey((k) => (r.phases.some((p) => p.key === k) ? k : r.phases[0]?.key ?? ''))
      })
      .catch((e) => setFehler(apiErrorText(e, 'Die Cockpits konnten nicht geladen werden.')))
    fetchUsers().then(setBenutzer).catch(() => undefined)
    fetchSites().then(setStandorte).catch(() => undefined)
  }, [])

  useEffect(() => {
    if (!phaseKey) return
    try {
      localStorage.setItem(ansichtKey(user?.id), JSON.stringify({ phaseKey, sicht, filterOffen } satisfies Ansicht))
    } catch {
      // privates Fenster o. Ä. — dann eben ohne Gedächtnis
    }
  }, [phaseKey, sicht, filterOffen, user?.id])

  const felder: FilterFieldDef[] = useMemo(
    () => [
      { key: 'search', label: 'Suche', type: 'text', category: 'core', operators: ['contains'] },
      // Umgekehrt benannt: Die Vorgabe ist „nur freigegebene" (Abnahme-Frage 8), und ein
      // Boolean-Filter ohne Wert zeigt „Alle" — das hätte das Gegenteil behauptet.
      { key: 'includePreparation', label: 'auch Positionen in Vorbereitung', type: 'boolean', category: 'core', operators: ['is'] },
      { key: 'statuses', label: 'Status', type: 'select-multi', category: 'core', options: STATUS_OPTIONEN, operators: ['is'] },
      {
        key: 'userId',
        label: 'eingeteilt',
        type: 'select',
        category: 'core',
        options: benutzer.map((b) => ({ value: b.id, label: b.name })),
        operators: ['is'],
      },
      { key: 'unassigned', label: 'nicht eingeteilt', type: 'boolean', category: 'core', operators: ['is'] },
      { key: 'dueTo', label: 'fällig bis', type: 'date', category: 'core', operators: ['is'] },
      {
        key: 'siteId',
        label: 'Betriebsstätte',
        type: 'select',
        category: 'project',
        options: standorte.map((s) => ({ value: s.id, label: s.name })),
        operators: ['is'],
      },
      { key: 'onlyMine', label: `nur meine ${terms.projectPlural}`, type: 'boolean', category: 'project', operators: ['is'] },
    ],
    [benutzer, standorte, terms.projectPlural],
  )

  /** Die Filter als Query — ein stabiles Objekt, damit Liste und Board nicht im Kreis laden. */
  const filter: CockpitFilter = useMemo(() => {
    const f: CockpitFilter = {}
    for (const [key, aktiv] of Object.entries(filterValues)) {
      if (!aktiv.value) continue
      if (key === 'includePreparation') f.onlyReleasedPositions = aktiv.value !== 'true'
      else if (['unassigned', 'onlyMine'].includes(key)) f[key] = aktiv.value === 'true'
      else if (key === 'dueTo') f.dueTo = aktiv.value.slice(0, 10)
      else f[key] = aktiv.value
    }
    return f
  }, [filterValues])

  const filterInfo = useMemo(
    () =>
      Object.values(filterValues)
        .filter((f) => f.value)
        .map((f) => `${felder.find((x) => x.key === f.fieldKey)?.label ?? f.fieldKey}: ${f.value}`)
        .join(' · '),
    [filterValues, felder],
  )

  const ladeBoard = useCallback(async () => {
    if (!phaseKey) return
    try {
      setBoard(await fetchBoard(phaseKey, filter))
      setFehler(null)
    } catch (e) {
      setFehler(apiErrorText(e, 'Das Cockpit konnte nicht geladen werden.'))
    }
  }, [phaseKey, filter])

  useEffect(() => {
    void ladeBoard()
  }, [ladeBoard, version])

  const geaendert = useCallback(async () => {
    setVersion((v) => v + 1)
    if (detail) {
      try {
        const neu = await fetchCard(detail.karte.stepId)
        setDetail((d) => (d ? { ...d, karte: neu } : d))
      } catch {
        setDetail(null)
      }
    }
  }, [detail])

  const oeffneAusBoard = (stepId: string) => {
    const karte = board?.cards.find((k) => k.stepId === stepId)
    if (karte) setDetail({ karte, liste: board!.cards })
  }

  const oeffneEinzeln = async (stepId: string) => {
    try {
      const karte = await fetchCard(stepId)
      setDetail({ karte, liste: [karte] })
    } catch (e) {
      setFehler(apiErrorText(e, 'Der Schritt konnte nicht geladen werden.'))
    }
  }

  const anzahlFilter = filterAnzahl(filterValues)
  const index = detail ?detail.liste.findIndex((k) => k.stepId === detail.karte.stepId) : -1
  const phase = phasen.find((p) => p.key === phaseKey)
  const s = board?.summary

  return (
    <div className={pageWide}>
      <div className="flex flex-col items-start justify-between gap-2 sm:flex-row sm:items-end">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Cockpits</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Arbeitsschritte je Phase über alle {terms.projectPlural} — einteilen, ordnen, die Woche planen.
            {phase?.responsibleName && <> Leitung: {phase.responsibleName}.</>}
          </p>
        </div>
        {s && (
          <div className="flex flex-wrap gap-3 text-sm">
            <Zahl label="Karten" wert={s.total} />
            <Zahl label="nicht freigegeben" wert={s.notReleased} />
            <Zahl label="nicht eingeteilt" wert={s.unassigned} betont={s.unassigned > 0} />
            <Zahl label="überfällig" wert={s.overdue} warnung={s.overdue > 0} />
            {s.materialMissing > 0 && <Zahl label="Material fehlt" wert={s.materialMissing} warnung />}
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-end justify-between gap-2 border-b border-border">
        <div className="flex flex-wrap gap-1">
          {phasen.map((p) => (
            <button
              key={p.key}
              type="button"
              onClick={() => {
                setPhaseKey(p.key)
                setDetail(null)
              }}
              className={`-mb-px flex items-center gap-1.5 border-b-2 px-4 py-2 text-sm ${
                phaseKey === p.key ? 'border-primary font-medium text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: p.color ?? 'hsl(var(--muted-foreground))' }} />
              {p.name}
            </button>
          ))}
        </div>
        <div className="mb-1 flex items-center gap-2">
          {sicht !== 'week' && (
            <button
              type="button"
              aria-expanded={filterOffen}
              aria-label={anzahlFilter ? `Filter — ${anzahlFilter} aktiv` : 'Filter'}
              title={filterOffen ? 'Filterleiste ausblenden' : 'Filterleiste einblenden'}
              className={`inline-flex items-center gap-1 rounded-md border border-border px-3 py-1 text-sm hover:bg-muted ${
                anzahlFilter ? 'border-primary bg-primary/10 font-medium text-primary hover:bg-primary/15' : ''
              } ${filterOffen && !anzahlFilter ? 'bg-muted' : ''}`}
              onClick={() => setFilterOffen((o) => !o)}
            >
              <Filter size={14} className={anzahlFilter ? 'fill-current' : ''} /> Filter
              {anzahlFilter > 0 && <span className="rounded-full bg-primary px-1.5 text-[11px] leading-4 text-primary-foreground">{anzahlFilter}</span>}
              {filterOffen ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
            </button>
          )}
          <div className="flex rounded-md border border-border p-0.5">
            {SICHTEN.map((x) => (
              <button
                key={x.key}
                type="button"
                onClick={() => setSicht(x.key)}
                className={`rounded px-3 py-1 text-sm ${sicht === x.key ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
              >
                {x.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Bleibt eingeklappt eingehängt: Sie stellt die zuletzt benutzten Filter wieder her, und die Filter gelten weiter. */}
      {sicht !== 'week' && (
        <div className={filterOffen ? '' : 'hidden'}>
          <FilterBar availableFields={felder} value={filterValues} onChange={setFilterValues} layoutKey="work-cockpit" defaultFieldKeys={['search', 'statuses', 'unassigned']} />
        </div>
      )}

      {fehler && <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{fehler}</p>}

      {phaseKey && sicht === 'board' && board && (
        <CockpitBoard
          phaseKey={phaseKey}
          board={board}
          filter={filter}
          darfOrdnen={darfEinteilen}
          darfFreigeben={darfFreigeben}
          onReload={ladeBoard}
          onOpen={oeffneAusBoard}
          onEinteilen={(stepIds) => setEinteilen({ stepIds })}
        />
      )}

      {phaseKey && sicht === 'list' && (
        <CockpitList
          phaseKey={phaseKey}
          phaseName={phase?.name ?? ''}
          filter={filter}
          filterInfo={filterInfo}
          darfEinteilen={darfEinteilen}
          darfFreigeben={darfFreigeben}
          darfStatus={darfStatus}
          version={version}
          onOpen={(karte, liste) => setDetail({ karte, liste })}
          onEinteilen={(stepIds) => setEinteilen({ stepIds })}
          onChanged={() => void geaendert()}
        />
      )}

      {phaseKey && sicht === 'week' && (
        <CockpitWeek
          phaseKey={phaseKey}
          darfEinteilen={darfEinteilen}
          version={version}
          onEinteilen={(stepIds, vorbelegt) => setEinteilen({ stepIds, vorbelegt })}
          onOpenStep={(stepId) => void oeffneEinzeln(stepId)}
        />
      )}

      {!phaseKey && phasen.length === 0 && !fehler && <p className="text-sm text-muted-foreground">Keine aktive Phase — die Phasen stehen in den Einstellungen.</p>}

      {detail && (
        <CockpitDetailPanel
          karte={detail.karte}
          onClose={() => setDetail(null)}
          onPrev={index > 0 ? () => setDetail({ ...detail, karte: detail.liste[index - 1] }) : undefined}
          onNext={index >= 0 && index < detail.liste.length - 1 ? () => setDetail({ ...detail, karte: detail.liste[index + 1] }) : undefined}
          onChanged={() => void geaendert()}
          onEinteilen={() => setEinteilen({ stepIds: [detail.karte.stepId] })}
        />
      )}

      {einteilen && (
        <AssignDialog
          stepIds={einteilen.stepIds}
          vorbelegt={einteilen.vorbelegt}
          onClose={() => setEinteilen(null)}
          onDone={() => {
            setEinteilen(null)
            void geaendert()
          }}
        />
      )}
    </div>
  )
}

function Zahl({ label, wert, warnung, betont }: { label: string; wert: number; warnung?: boolean; betont?: boolean }) {
  return (
    <div className="rounded-md border border-border bg-card px-3 py-1 text-center">
      <div className={`text-lg font-semibold tabular-nums ${warnung ? 'text-destructive' : betont ? 'text-primary' : ''}`}>{wert}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </div>
  )
}
